import { apiClient } from '../../../api/client'
import { downloadXlsx } from '../../../lib/download'
import type {
  NotificationTemplate,
  NotificationTemplateUpsert,
  NotificationTypeOption,
  OperationCodeOption,
  PagedResponse,
  TemplateParameterOption,
  RecordStatusBody,
} from '../types'

const BASE = '/management/notification-templates'

export interface NotificationTemplateQuery {
  /** İşlem kodu filtresi; `null` = Tümü → gönderilmez. */
  operationCodeId?: number | null
  /** Bildirim türü filtresi; `null` = Tümü → gönderilmez. */
  notificationTypeId?: number | null
  page: number
  size: number
  /**
   * Kolon sıralaması (`alan,asc|desc`); yoksa gönderilmez → sunucu varsayılanı (işlem kodu, tür,
   * kimlik). Değer `useTableSort`'tan gelir; sunucu beyaz listesinde olmayan alan sessizce düşürülür.
   */
  sort?: string
}

/**
 * Şablon listesi (059) — **sunucu-tarafı** sayfalama + filtreleme + **kolon sıralaması**. İki filtre
 * VE mantığıyla birleşir.
 *
 * Yanıttaki `contentTr`/`contentEn` **daima `null`'dır** — form açılırken
 * `getNotificationTemplate(id)` çağrılır.
 */
export async function getNotificationTemplates({
  operationCodeId,
  notificationTypeId,
  page,
  size,
  sort,
}: NotificationTemplateQuery): Promise<PagedResponse<NotificationTemplate>> {
  const { data } = await apiClient.get<PagedResponse<NotificationTemplate>>(BASE, {
    params: {
      page,
      size,
      operationCodeId: operationCodeId ?? undefined,
      notificationTypeId: notificationTypeId ?? undefined,
      sort,
    },
  })
  return data
}

/** İşlem Kodu combo seçenekleri — yalnız Bildirimler modülünün aktif kodları. */
export async function getTemplateOperationCodes(): Promise<OperationCodeOption[]> {
  const { data } = await apiClient.get<OperationCodeOption[]>(`${BASE}/operation-codes`)
  return data
}

/** Bildirim Türü combo seçenekleri; `email` bayrağı mail alanlarının açık/kapalı olmasını belirler. */
export async function getTemplateNotificationTypes(): Promise<NotificationTypeOption[]> {
  const { data } = await apiClient.get<NotificationTypeOption[]>(`${BASE}/notification-types`)
  return data
}

/** Tekil kayıt — form açılışı. Listeden farkı: `contentTr`/`contentEn` **dolu** gelir. */
export async function getNotificationTemplate(id: number): Promise<NotificationTemplate> {
  const { data } = await apiClient.get<NotificationTemplate>(`${BASE}/${id}`)
  return data
}

/**
 * Şablon formundaki parametre panelini besleyen katalog (FR-021a).
 *
 * Uç bu kaynakta yer aldığı için **şablon yetkisiyle** erişilir — yalnız `NOTIFICATION_TEMPLATE_MANAGE`
 * yetkisi olan kullanıcıda panel boş gelmez (spec FR-002).
 */
export async function getTemplateParameterPalette(): Promise<TemplateParameterOption[]> {
  const { data } = await apiClient.get<TemplateParameterOption[]>(`${BASE}/parameters`)
  return data
}

/** Yeni şablon (201). Sunucu mail alanlarını normalize eder ve e-posta dışı türde boşaltır. */
export async function createNotificationTemplate(
  body: NotificationTemplateUpsert,
): Promise<NotificationTemplate> {
  const { data } = await apiClient.post<NotificationTemplate>(BASE, body)
  return data
}

/** Şablon güncelleme; `modDate` optimistic lock için zorunludur. */
export async function updateNotificationTemplate(
  id: number,
  body: NotificationTemplateUpsert,
): Promise<NotificationTemplate> {
  const { data } = await apiClient.put<NotificationTemplate>(`${BASE}/${id}`, body)
  return data
}

/** Durum degistir - yalniz 0 (Pasif) <-> 1 (Aktif); `modDate` optimistic lock icin zorunlu. */
export async function changeNotificationTemplateStatus(
  id: number,
  body: RecordStatusBody,
): Promise<NotificationTemplate> {
  const { data } = await apiClient.put<NotificationTemplate>(`${BASE}/${id}/status`, body)
  return data
}

/** Soft-delete (record_status = 2). Onay adimi istemcidedir; `modDate` istenmez. */
export async function removeNotificationTemplate(id: number): Promise<void> {
  await apiClient.delete(`${BASE}/${id}`)
}

/**
 * Bildirim şablonu listesini Excel (.xlsx) olarak indirir.
 *
 * Liste ucuyla aynı filtreler; sunucu **tüm** filtreli kayıtları döndürür (sayfalama yok). İndirme
 * mekaniği ve dosya adı ortak {@link downloadXlsx} yardımcısındadır (boş filtreler gönderilmez; ad
 * sunucunun Content-Disposition'ından okunur).
 */
export async function exportNotificationTemplates(operationCodeId?: number, notificationTypeId?: number, sort?: string): Promise<void> {
  await downloadXlsx(`${BASE}/export`, { operationCodeId, notificationTypeId, sort }, 'bildirim-sablonlari.xlsx')
}
